package breadcrumbs

import apiclient.FormationClient
import apiclient.geoobjects.*
import apiclient.tags.getUniqueTag
import apiclient.util.simpleCache
import apiclient.validations.parseEnumValue
import data.objects.ActiveObjectStore
import koin.withKoin
import kotlinx.coroutines.flow.Flow
import kotlin.time.Duration.Companion.minutes

/**
 *  Map of objectId to ParentObject
 */
class BreadcrumbsCache() {

    private val cache =
        simpleCache<String, GeoObjectDetails>(capacity = 1000, maxAgeInMillis = 5.minutes.inWholeMilliseconds)

    var lastObject: GeoObjectDetails? = null

    fun lastObjectIsChildOf(currentObjectId: String?): Boolean {
        return getParentId(lastObject)?.let { parentIdOfLastObj ->
            currentObjectId?.let {
                cache[parentIdOfLastObj]?.id == currentObjectId
            }
        } ?: false
    }

    fun getParentId(obj: GeoObjectDetails?): String? {
        return when (parseEnumValue<ObjectType>(
            obj?.tags?.getUniqueTag(ObjectTags.ConnectedObjectType),
        )) {
            ObjectType.Building, ObjectType.Floor, ObjectType.Unit -> {
                obj?.tags?.getUniqueTag(BuildingAndFloorTags.BuildingId)
            }

            else -> {
                obj?.tags?.getUniqueTag(ObjectTags.ConnectedTo)
            }
        }
    }

    // Return a flow that adds parents incrementally, so the UI can receive and re-render as each parent is fetched.
    fun getBreadCrumbs(obj: GeoObjectDetails?): Flow<List<GeoObjectDetails>> {
        withKoin {
            val activeObjectStore: ActiveObjectStore = get()
            val currentObject = obj ?: activeObjectStore.current

            lastObject = currentObject

            return kotlinx.coroutines.flow.channelFlow {
                val breadcrumbList = mutableListOf<GeoObjectDetails>()

                suspend fun fetchParentsRecursively(current: GeoObjectDetails, depth: Int = 0) {

                    if (depth >= 5) return

                    val parentId = getParentId(current) ?: return
                    if (parentId == current.id) return // Prevent self-reference
                    val parent = getParent(parentId) ?: return

                    breadcrumbList.add(parent)
                    send(breadcrumbList.toList().reversed())
                    fetchParentsRecursively(parent, depth + 1)
                }

//                breadcrumbList.add(currentObject)
                send(breadcrumbList.toList())
                fetchParentsRecursively(currentObject)
            }
        }
    }

    suspend fun getParent(parentId: String): GeoObjectDetails? {
        withKoin {
            val formationClient: FormationClient = get()
            return cache.getOrFetch(parentId) {
                formationClient.restGetObjectById(parentId).getOrNull()
            }
        }
    }
}
